import { StateRegulatoryStatus } from "encamp-prisma/generated/browser";

/**
 * For each state, get its generator status code to enum mapping
 * The source of this is: https://docs.google.com/spreadsheets/d/1CQ4MPwKxKx21oO89-9JT_QpE4hfwcsCihDKdGH28crA/edit?gid=1116429521#gid=1116429521
 */
const STATE_GENERATOR_STATUS_CODE_TO_LABEL: Record<
  string,
  {
    code: string;
    description: string;
    stateStatus: StateRegulatoryStatus | null;
  }[]
> = {
  AK: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  AL: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "A", description: "ACTIVE", stateStatus: null },
    { code: "D", description: "", stateStatus: null },
    {
      code: "P",
      description: "Pending Based on Waste Analysis Results",
      stateStatus: StateRegulatoryStatus.PENDING,
    },
  ],
  AR: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    { code: "6", description: "", stateStatus: null },
    { code: "7", description: "", stateStatus: null },
    { code: "8", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  AS: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "D", description: "", stateStatus: null },
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  AZ: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "D", description: "", stateStatus: null },
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  CA: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "D",
      description: "Deactivated Generator (DAG)",
      stateStatus: null,
    },
    { code: "F", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator, Verified",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  CO: [
    {
      code: "0",
      description: "Regulated under another ID number",
      stateStatus: null,
    },
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Definitionally excluded wastes",
      stateStatus: null,
    },
    { code: "3", description: "Delisted wastes", stateStatus: null },
    { code: "4", description: "One-time generator", stateStatus: null },
    { code: "5", description: "Periodic generator", stateStatus: null },
    {
      code: "6",
      description: "No longer generating hazardous waste, still in business",
      stateStatus: null,
    },
    {
      code: "7",
      description: "No longer generating hazardous waste, out of business",
      stateStatus: null,
    },
    {
      code: "8",
      description: "Never generated hazardous waste",
      stateStatus: null,
    },
    {
      code: "9",
      description: "ID number to transport non-hazardous waste",
      stateStatus: null,
    },
    {
      code: "F",
      description: "Same as Federally Defined",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  CT: [
    {
      code: "E",
      description: "Equal to Federal Generator Status - Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  DC: [
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    { code: "5", description: "", stateStatus: null },
    { code: "6", description: "", stateStatus: null },
    { code: "8", description: "", stateStatus: null },
    { code: "D", description: "Code deleted", stateStatus: null },
    {
      code: "E",
      description: "Same as Federal Code",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    { code: "L", description: "", stateStatus: null },
  ],
  DE: [
    { code: "6", description: "", stateStatus: null },
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  FL: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Conditionally Exempt Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.CONDITIONALLY_EXEMPT_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "N",
      description: "Not a Generator - Verified",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    { code: "V", description: "Vessel (Cruise Ship)", stateStatus: null },
  ],
  GA: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "4", description: "", stateStatus: null },
    {
      code: "F",
      description: "FEDERALLY DEFINED GENERATOR",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  GU: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "D", description: "", stateStatus: null },
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  HI: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "D", description: "", stateStatus: null },
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  HQ: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: null,
    },
    {
      code: "C",
      description: "Converted; no activity data available.",
      stateStatus: null,
    },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    {
      code: "P",
      description: "Pending Based on Waste Analysis Results",
      stateStatus: StateRegulatoryStatus.PENDING,
    },
    {
      code: "U",
      description: "Undetermined by the Implementer",
      stateStatus: StateRegulatoryStatus.UNDETERMINED,
    },
  ],
  IA: [
    {
      code: "0",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  ID: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Conditionally Exempt SQG",
      stateStatus: StateRegulatoryStatus.CONDITIONALLY_EXEMPT_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    {
      code: "S",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    {
      code: "U",
      description: "Undetermined by the Implementer",
      stateStatus: StateRegulatoryStatus.UNDETERMINED,
    },
  ],
  IL: [
    {
      code: "1",
      description: "LQG",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "VSQG",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    {
      code: "S",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    {
      code: "0",
      description: "Highest Status in 2019: Did Not Generate Hazardous Waste",
      stateStatus: null,
    },
    {
      code: "4",
      description:
        "Large Quantity Generator- Generates more than 2,200lbs of hazardous waste in any calendar month during a year or a small quantity generator that has more than 6,000kg of hazardous waste accumulated on site. See regulations for acute waste.",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "5",
      description:
        "Small Quantity Generator- Generates less than 2,200lbs of hazardous waste but greater than 220lbs in any calendar month during a year and has no more than 6,000kg of hazardous waste accumulated on-site. See regulations for acute waste.",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "6",
      description:
        "Very Small Quantity Generator- Generates less than 220lbs of hazardous waste in any calendar month during a year or has no more than 2,200lbs accumulated onsite. See regulations for acute and severely toxic waste limits.",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "7", description: "", stateStatus: null },
    {
      code: "8",
      description:
        "Not a Generator- Did not generate any hazardous waste during any calendar month during a year.",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    {
      code: "A",
      description: "Highest Status in 2020: Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "B",
      description: "Highest Status in 2020: Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "D",
      description: "Highest Status in 2020: Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "E",
      description: "Highest Status in 2020: Did Not Generate Hazardous Waste",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    {
      code: "F",
      description: "Highest Status in 2021: Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "G",
      description: "Highest Status in 2021: Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "H",
      description: "Highest Status in 2021: Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "I",
      description: "Highest Status in 2021: Did Not Generate Hazardous Waste",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    { code: "J", description: "", stateStatus: null },
    {
      code: "K",
      description: "Highest Status in 2022: Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "L",
      description: "Highest Status in 2019: Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "M",
      description: "Highest Status in 2022: Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "O",
      description: "Highest Status in 2022: Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "Q",
      description: "Highest Status in 2022: Did Not Generate Hazardous Waste",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    { code: "R", description: "", stateStatus: null },
    {
      code: "S",
      description: "Highest Status in 2019: Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    { code: "T", description: "", stateStatus: null },
    {
      code: "V",
      description: "Highest Status in 2019: Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "X", description: "", stateStatus: null },
    { code: "Y", description: "", stateStatus: null },
  ],
  KS: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Conditionally Exempt Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.CONDITIONALLY_EXEMPT_SMALL_QUANTITY_GENERATOR,
    },
    { code: "5", description: "", stateStatus: null },
    { code: "7", description: "", stateStatus: null },
    { code: "9", description: "", stateStatus: null },
    { code: "C", description: "", stateStatus: null },
    { code: "G", description: "", stateStatus: null },
    { code: "K", description: "", stateStatus: null },
    { code: "L", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    { code: "Q", description: "", stateStatus: null },
  ],
  KY: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    { code: "4", description: "", stateStatus: null },
    { code: "5", description: "", stateStatus: null },
    { code: "6", description: "", stateStatus: null },
    { code: "7", description: "", stateStatus: null },
    { code: "8", description: "", stateStatus: null },
    { code: "9", description: "", stateStatus: null },
    { code: "C", description: "", stateStatus: null },
    { code: "D", description: "", stateStatus: null },
    { code: "E", description: "", stateStatus: null },
    {
      code: "S",
      description: "SAME AS FEDERAL",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  LA: [
    { code: "4", description: "", stateStatus: null },
    { code: "5", description: "", stateStatus: null },
    { code: "6", description: "", stateStatus: null },
    { code: "7", description: "", stateStatus: null },
    { code: "9", description: "", stateStatus: null },
    { code: "E", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    { code: "V", description: "", stateStatus: null },
    { code: "W", description: "", stateStatus: null },
  ],
  MA: [
    { code: "0", description: "", stateStatus: null },
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "E",
      description: "Equal to Federal Generator Status - Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  MD: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "5",
      description: "Maryland Fully Regulated Generator",
      stateStatus: StateRegulatoryStatus.MARYLAND_GENERATOR_FULLY_REGULATED,
    },
    {
      code: "6",
      description: "MD Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    { code: "7", description: "", stateStatus: null },
    { code: "8", description: "", stateStatus: null },
    {
      code: "9",
      description: "Non-Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    { code: "M", description: "", stateStatus: null },
  ],
  ME: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "E",
      description: "Equal to Federal Generator Status - Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    {
      code: "F",
      description: "ME Full Quantity Generator (Fed LQG, SQG)",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "N",
      description: "Not a Generator of Federal RCRA Waste",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    {
      code: "P",
      description: "ME Small Quantity Generator Plus (SQG+) (Fed VSQG w/additional accumulation)",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "S",
      description: "ME Small Quantity Generator (Fed VSQG)",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
  ],
  MI: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    {
      code: "S",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  MN: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "4", description: "", stateStatus: null },
    {
      code: "5",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "6",
      description: "Minimal Quantity Generator",
      stateStatus: StateRegulatoryStatus.MINI_QUANTITY_GENERATOR,
    },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  MO: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  MP: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "D", description: "", stateStatus: null },
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  MS: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "C", description: "", stateStatus: null },
    { code: "L", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    { code: "S", description: "", stateStatus: null },
    {
      code: "U",
      description: "Undetermined by the Implementer",
      stateStatus: StateRegulatoryStatus.UNDETERMINED,
    },
    { code: "V", description: "", stateStatus: null },
  ],
  MT: [
    { code: "0", description: "", stateStatus: null },
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "4", description: "", stateStatus: null },
    { code: "5", description: "", stateStatus: null },
    { code: "6", description: "", stateStatus: null },
    { code: "7", description: "", stateStatus: null },
    { code: "8", description: "", stateStatus: null },
    { code: "9", description: "", stateStatus: null },
    { code: "E", description: "", stateStatus: null },
    {
      code: "F",
      description: "Same as Federally Defined",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    { code: "H", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    {
      code: "S",
      description: "Non-Notifier Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
  ],
  NC: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    {
      code: "M",
      description: "MERCURY SWITCH REMOVAL PROGRAM",
      stateStatus: null,
    },
  ],
  ND: [
    {
      code: "0",
      description: "Regulated under another ID number.",
      stateStatus: null,
    },
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "4", description: "", stateStatus: null },
    { code: "5", description: "", stateStatus: null },
    {
      code: "6",
      description: "No longer generates hazardous waste, still in business.",
      stateStatus: null,
    },
    { code: "7", description: "", stateStatus: null },
    { code: "8", description: "", stateStatus: null },
    {
      code: "F",
      description: "Same as Federally defined.",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    { code: "H", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    {
      code: "U",
      description: "Undetermined by the Implementer",
      stateStatus: StateRegulatoryStatus.UNDETERMINED,
    },
  ],
  NE: [
    {
      code: "1",
      description: "LQG",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "SQG",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "CESQG",
      stateStatus: StateRegulatoryStatus.CONDITIONALLY_EXEMPT_SMALL_QUANTITY_GENERATOR,
    },
    { code: "4", description: "Deactivated ID#", stateStatus: null },
    { code: "5", description: "", stateStatus: null },
    { code: "7", description: "", stateStatus: null },
    {
      code: "N",
      description: "Facility Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    {
      code: "S",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  NH: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "4", description: "", stateStatus: null },
    { code: "7", description: "", stateStatus: null },
    {
      code: "E",
      description: "Equal to Federal Generator Status - Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    { code: "F", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    { code: "S", description: "", stateStatus: null },
  ],
  NJ: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "4", description: "", stateStatus: null },
    { code: "9", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    {
      code: "S",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    { code: "X", description: "", stateStatus: null },
  ],
  NM: [
    { code: "5", description: "", stateStatus: null },
    { code: "6", description: "", stateStatus: null },
    { code: "7", description: "", stateStatus: null },
    { code: "8", description: "", stateStatus: null },
    { code: "C", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  NN: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "D", description: "", stateStatus: null },
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  NV: [
    {
      code: "1",
      description: "LQG",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    { code: "D", description: "", stateStatus: null },
    {
      code: "F",
      description: "Federal Gen. Status Only",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    { code: "I", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    { code: "R", description: "", stateStatus: null },
  ],
  NY: [
    {
      code: "1",
      description: "LQG for NYS PCB B001-7 HW plus RCRA HW",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    { code: "4", description: "", stateStatus: null },
    {
      code: "5",
      description: "SQG for NYS PCB B001-7 HW plus RCRA HW",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "6",
      description: "VSQG for NYS PCB B001-7 HW plus RCRA HW",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "9", description: "", stateStatus: null },
    { code: "N", description: "Not a PCB HW generator", stateStatus: null },
    { code: "S", description: "", stateStatus: null },
  ],
  OH: [
    { code: "4", description: "", stateStatus: null },
    { code: "5", description: "PCB Activity", stateStatus: null },
    {
      code: "S",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    {
      code: "U",
      description: "Undetermined by the Implementer",
      stateStatus: null,
    },
  ],
  OK: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "4", description: "", stateStatus: null },
    { code: "5", description: "", stateStatus: null },
    { code: "6", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  OR: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    {
      code: "S",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  PA: [
    {
      code: "1",
      description: "LQG",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "SQG",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "CEG",
      stateStatus: StateRegulatoryStatus.CONDITIONALLY_EXEMPT_SMALL_QUANTITY_GENERATOR,
    },
    { code: "4", description: "", stateStatus: null },
    { code: "5", description: "", stateStatus: null },
    { code: "6", description: "", stateStatus: null },
    { code: "7", description: "Used Oil Generator", stateStatus: null },
    { code: "9", description: "", stateStatus: null },
    { code: "E", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  PR: [
    {
      code: "S",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  RI: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "E",
      description: "Equal to Federal Generator Status - Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    { code: "S", description: "", stateStatus: null },
  ],
  SC: [
    {
      code: "4",
      description: "Same As Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  SD: [
    { code: "0", description: "", stateStatus: null },
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "4",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    { code: "5", description: "", stateStatus: null },
    { code: "6", description: "", stateStatus: null },
    { code: "7", description: "", stateStatus: null },
    { code: "8", description: "", stateStatus: null },
    { code: "9", description: "", stateStatus: null },
    { code: "F", description: "", stateStatus: null },
  ],
  TN: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "4", description: "", stateStatus: null },
    { code: "5", description: "", stateStatus: null },
    { code: "6", description: "", stateStatus: null },
    { code: "7", description: "", stateStatus: null },
    { code: "8", description: "", stateStatus: null },
    { code: "9", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  TT: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "D", description: "", stateStatus: null },
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  TX: [
    { code: "4", description: "", stateStatus: null },
    { code: "5", description: "", stateStatus: null },
    { code: "9", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  UT: [
    { code: "0", description: "", stateStatus: null },
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "4", description: "", stateStatus: null },
    { code: "5", description: "", stateStatus: null },
    { code: "6", description: "", stateStatus: null },
    { code: "7", description: "", stateStatus: null },
    { code: "8", description: "", stateStatus: null },
    { code: "9", description: "", stateStatus: null },
    {
      code: "F",
      description: "Same as federally defined",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  VA: [
    { code: "4", description: "", stateStatus: null },
    { code: "5", description: "", stateStatus: null },
    { code: "6", description: "", stateStatus: null },
    { code: "A", description: "", stateStatus: null },
  ],
  VI: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "4", description: "", stateStatus: null },
    { code: "9", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    {
      code: "S",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
    { code: "X", description: "", stateStatus: null },
  ],
  VT: [
    {
      code: "E",
      description: "Equal to Federal Generator Status - Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  WA: [
    {
      code: "1",
      description: "LQG - Large Quantity Generator (Greater than 2,200 lbs/mo)",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "MQG - Medium Quantity Generator (Between 220 - 2,200 lbs/mo)",
      stateStatus: StateRegulatoryStatus.MEDIUM_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "SQG - Small Quantity Generator (Less than 220 lbs/mo)",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    { code: "F", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    {
      code: "X",
      description: "XQG - Not a Generator (No Regulated Waste Generated)",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  WI: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "4", description: "", stateStatus: null },
    { code: "5", description: "", stateStatus: null },
    { code: "6", description: "", stateStatus: null },
    { code: "7", description: "", stateStatus: null },
    { code: "8", description: "", stateStatus: null },
    { code: "9", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
  ],
  WV: [
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    { code: "4", description: "Active Facility", stateStatus: null },
    { code: "5", description: "", stateStatus: null },
    { code: "6", description: "", stateStatus: null },
    { code: "7", description: "", stateStatus: null },
    { code: "8", description: "", stateStatus: null },
    {
      code: "9",
      description: "Used Oil Only - Not a Generator of HW",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    { code: "A", description: "", stateStatus: null },
    {
      code: "N",
      description: "Not a Generator",
      stateStatus: StateRegulatoryStatus.NOT_A_GENERATOR,
    },
    {
      code: "S",
      description: "SAME AS FEDERAL",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  WY: [
    { code: "0", description: "", stateStatus: null },
    {
      code: "1",
      description: "Large Quantity Generator",
      stateStatus: StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR,
    },
    {
      code: "2",
      description: "Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR,
    },
    {
      code: "3",
      description: "Very Small Quantity Generator",
      stateStatus: StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR,
    },
    { code: "4", description: "", stateStatus: null },
    { code: "5", description: "", stateStatus: null },
    { code: "6", description: "", stateStatus: null },
    { code: "7", description: "", stateStatus: null },
    { code: "8", description: "", stateStatus: null },
    { code: "9", description: "", stateStatus: null },
    { code: "F", description: "", stateStatus: null },
  ],
  XA: [
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  XB: [
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  XC: [
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  XD: [
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  XE: [
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  XF: [
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  XG: [
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  XH: [
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  XI: [
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
  XJ: [
    {
      code: "F",
      description: "Same as Federal",
      stateStatus: StateRegulatoryStatus.SAME_AS_FEDERAL,
    },
  ],
};

const STATE_TO_GENERATOR_STATUS_CODE_TO_ENUM_MAP = new Map(
  Object.entries(STATE_GENERATOR_STATUS_CODE_TO_LABEL).map(([state, codes]) => [
    state,
    new Map(codes.map((code) => [code.code, code.stateStatus])),
  ])
);

const STATES_WITH_INDEPENDENT_STATE_GENERATOR_STATUS = new Set([
  "AL",
  "CA",
  "IN",
  "KS",
  "MA",
  "MD",
  "ME",
  "MN",
  "NE",
  "NY",
  "SC",
  "WA",
  "WI",
]);

/**
 * Flat (state, code) → enum entries, skipping codes with no status — lets SQL be
 * generated from the same table `getStateGeneratorStatusCodeToEnum` reads.
 */
export const getStateGeneratorStatusCodeEntries = (
  states: readonly string[]
): { state: string; code: string; stateStatus: StateRegulatoryStatus }[] =>
  states.flatMap((state) =>
    (STATE_GENERATOR_STATUS_CODE_TO_LABEL[state] ?? []).flatMap(({ code, stateStatus }) =>
      stateStatus === null ? [] : [{ state, code, stateStatus }]
    )
  );

export const getStateGeneratorStatusCodeToEnum = (
  state: string,
  code: string
): StateRegulatoryStatus => {
  const stateCodes = STATE_TO_GENERATOR_STATUS_CODE_TO_ENUM_MAP.get(state);
  if (!stateCodes) {
    return StateRegulatoryStatus.UNDETERMINED;
  }
  const status = stateCodes.get(code);

  return status ?? StateRegulatoryStatus.UNDETERMINED;
};

export function stateHasIndependentGeneratorStatus(state: string | null | undefined): boolean {
  if (!state) {
    return false;
  }

  return STATES_WITH_INDEPENDENT_STATE_GENERATOR_STATUS.has(state.trim().toUpperCase());
}
